import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Steps from "./Steps";
import MilestoneCard from "./MilestoneCard";
import UploadSection from "./UploadSection";
import IssuesSection from "./IssuesSection";
import CInput from "../../../../../components/CInput";
import CAutocomplete from "../../../../../components/CAutocomplete";
import CDatePicker from "../../../../../components/CDatePicker";
import {
  getProjectScopesForUpdate,
  getActivitiesByScope,
  getScopeUpdates,
  getUpdatesFiles,
} from "../../../../../services/scopeUpdateService";

const TAB_STEPS = [
  { slug: "summary", title: "Summary", estimate: 2 },
  { slug: "activities", title: "Activity Photos", estimate: 3 },
  { slug: "milestones", title: "Impacted Milestones", estimate: 2 },
  { slug: "issues", title: "Issue Burndown", estimate: 4 },
  { slug: "review", title: "Review & Submit", estimate: 1 },
];

const parseMeta = (raw) => {
  if (raw == null) return { room: null, milestones: [] };
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || { room: null, milestones: [] };
    } catch {
      return { room: null, milestones: [] };
    }
  }
  return typeof raw === "object" ? raw : { room: null, milestones: [] };
};

export default function ProjectUpdateForm({
  projectId,
  projectDetails,
  updateIdFromUrl,
  scopeIdFromUrl,
  updateDateFromUrl,
  createdUpdateId,
  setCreatedUpdateId,
  activeTab,
  setActiveTab,
  isCreateMode,
  isViewMode,
  setIsViewMode,
  hasUpdateId,
  form,
  setForm,
  uploadedFiles,
  setUploadedFiles,
  selectedMilestones,
  setSelectedMilestones,
  milestoneProgress,
  setMilestoneProgress,
  milestoneNotes,
  setMilestoneNotes,
  onActivitiesForPayload,
  singleUpdateData,
  refetchSingleUpdate,
  refDataMilestones,
}) {
  const effectiveUpdateId = createdUpdateId || (updateIdFromUrl !== "new" ? updateIdFromUrl : null);
  const isEdit = !!effectiveUpdateId;

  const [scopesData, setScopesData] = useState(null);
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [updatesListForDateFilter, setUpdatesListForDateFilter] = useState(null);
  const galleryAvailable = hasUpdateId;
  const [activeSubTab, setActiveSubTab] = useState(
    galleryAvailable && !isCreateMode ? "gallery" : "annotate"
  );
  useEffect(() => {
    if (!galleryAvailable && activeSubTab === "gallery") {
      setActiveSubTab("annotate");
    }
  }, [galleryAvailable, activeSubTab]);

  useEffect(() => {
    if (!projectId) return;
    setIsLoadingScopes(true);
    getProjectScopesForUpdate(projectId)
      .then((res) => {
        if (res?.success) setScopesData(res);
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingScopes(false));
  }, [projectId]);

  /* For create mode: load updates list to filter out already-used scopes for the date */
  useEffect(() => {
    if (!isCreateMode || !projectId) return;
    getScopeUpdates(projectId, { page: 1, perPage: 100 })
      .then((res) => {
        if (res?.success) setUpdatesListForDateFilter(res);
      })
      .catch((err) => console.error(err));
  }, [isCreateMode, projectId]);

  const scopeOptions = useMemo(() => {
    if (!scopesData?.scopes || !Array.isArray(scopesData.scopes)) return [];
    const allowed = ["ongoing", "in_warranty"];
    return scopesData.scopes
      .filter((s) => s.bids?.some((b) => allowed.includes(b.status)))
      .map((scope) => {
        const bid = scope.bids.find((b) => allowed.includes(b.status));
        if (!bid) return null;
        return {
          scope_id: scope.scope_id,
          bid_project_id: bid.id,
          scope_name: scope.name || scope.scope_name || "Unnamed Scope",
        };
      })
      .filter(Boolean);
  }, [scopesData]);

  const scopeOptionsFilteredForDate = useMemo(() => {
    if (!isCreateMode) return scopeOptions;
    const formDateStr = form.date
      ? moment(form.date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    const dateGroup = (updatesListForDateFilter?.data ?? []).find(
      (g) => g.update_date === formDateStr
    );
    const usedIds = new Set(
      (dateGroup?.scope_updates ?? []).map((s) => s.scope_id)
    );
    return scopeOptions.filter((opt) => !usedIds.has(opt.scope_id));
  }, [isCreateMode, scopeOptions, form.date, updatesListForDateFilter]);

  /* Activities for selected scope */
  const scopeId = form.scope?.scope_id;
  useEffect(() => {
    if (!projectId || !scopeId) {
      setAvailableActivities([]);
      return;
    }
    setIsLoadingActivities(true);
    getActivitiesByScope(projectId, scopeId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const scopeData = list.find((s) => String(s.scope_id) === String(scopeId));
        setAvailableActivities(scopeData?.activities || []);
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingActivities(false));
  }, [projectId, scopeId]);

  useEffect(() => {
    onActivitiesForPayload?.(availableActivities ?? []);
  }, [availableActivities, onActivitiesForPayload]);

  /* Prefill scope from URL when editing */
  useEffect(() => {
    if (!scopeIdFromUrl || scopeOptions.length === 0) return;
    const sid = Number(scopeIdFromUrl);
    const opt = scopeOptions.find((o) => o.scope_id === sid);
    if (opt && !form.scope) {
      setForm((prev) => ({ ...prev, scope: opt }));
    }
  }, [scopeIdFromUrl, scopeOptions, form.scope, setForm]);

  /* Prefill from single update data (edit/view) */
  useEffect(() => {
    if (!singleUpdateData || isCreateMode) return;
    const data = singleUpdateData;
    const sid = data.scope_id ?? (scopeIdFromUrl ? Number(scopeIdFromUrl) : null);
    const matching = scopeOptions.find((o) => o.scope_id === sid);
    setForm({
      name: data.name || "",
      date: data.update_date ? new Date(data.update_date) : new Date(),
      description: data.message || "",
      scope:
        matching ||
        (sid != null
          ? {
              scope_id: sid,
              bid_project_id: data.bid_project_id,
              scope_name: data.scope_name || "",
            }
          : null),
    });
    if (Array.isArray(refDataMilestones)) {
      setSelectedMilestones(refDataMilestones);
      setMilestoneProgress((prev) => {
        const next = { ...prev };
        refDataMilestones.forEach((m) => {
          if (m.id != null) next[m.id] = m.progress ?? m.milestone_percentage ?? 0;
        });
        return next;
      });
      setMilestoneNotes((prev) => {
        const next = { ...prev };
        refDataMilestones.forEach((m) => {
          if (m.id != null && (m.notes ?? "").trim()) {
            next[m.id] = (m.notes ?? "").trim();
          }
        });
        return next;
      });
    }
  }, [singleUpdateData, isCreateMode, scopeOptions, refDataMilestones, scopeIdFromUrl, setForm, setMilestoneNotes, setMilestoneProgress, setSelectedMilestones]);

  /* When user switches to milestones tab, sync from server */
  useEffect(() => {
    if (activeTab !== "milestones" || !isEdit) return;
    refetchSingleUpdate?.();
  }, [activeTab, isEdit, refetchSingleUpdate]);

  /* Load files when updateId is available */
  useEffect(() => {
    if (!effectiveUpdateId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getUpdatesFiles(effectiveUpdateId);
        const list = Array.isArray(res?.data) ? res.data : [];
        const fromServer = list.map((item, index) => ({
          id: item.id,
          uniqueId: `design_${item.id}_${index}`,
          url: item.image_url,
          path: item.image_url,
          type: item.extension,
          file_extention: item.extension,
          meta_data: parseMeta(item.meta_data),
        }));
        if (cancelled) return;
        setUploadedFiles((prev) => {
          const pending = (prev || []).filter((f) => !f.id);
          return [...fromServer, ...pending];
        });
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveUpdateId, setUploadedFiles]);

  const milestoneIdsFromImages = useMemo(
    () => [
      ...new Set(
        (uploadedFiles || []).flatMap((f) =>
          (f.meta_data?.milestones || []).map((m) => m.id).filter(Boolean)
        )
      ),
    ],
    [uploadedFiles]
  );

  const steps = TAB_STEPS.map((s) => ({
    ...s,
    disabled: s.slug !== "summary" && !hasUpdateId,
  }));

  const selectedScope = form.scope;

  if (isLoadingScopes && !scopesData) {
    return (
      <div className="pud-page">
        <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
          Loading details...
        </div>
      </div>
    );
  }

  const visibleSteps = steps.map((s) =>
    s.slug === "review" ? { ...s, title: "Review" } : s
  );

  return (
    <>
      {isCreateMode ? (
        <Steps
          steps={steps}
          activeSlug={activeTab}
          onStepClick={(slug) => setActiveTab(slug)}
          isCreateMode={isCreateMode}
        />
      ) : (
        <div className="pud-tab-bar-row">
          <div className="pud-tab-scroll">
            {visibleSteps.map((s) => (
              <button
                key={s.slug}
                type="button"
                className={`pud-tab ${activeTab === s.slug ? "active" : ""}`}
                disabled={s.disabled}
                onClick={() => !s.disabled && setActiveTab(s.slug)}
              >
                {s.title}
              </button>
            ))}
          </div>
          <div className="pud-tab-bar-rhs">
            {!isViewMode && activeTab === "activities" && galleryAvailable ? (
              <>
                <button
                  type="button"
                  className={`pud-subtab ${activeSubTab === "annotate" ? "active" : ""}`}
                  onClick={() => setActiveSubTab("annotate")}
                >
                  Add Activity Photos
                </button>
                <button
                  type="button"
                  className={`pud-subtab ${activeSubTab === "gallery" ? "active" : ""}`}
                  onClick={() => setActiveSubTab("gallery")}
                >
                  Gallery
                </button>
              </>
            ) : (
              isViewMode &&
              hasUpdateId &&
              setIsViewMode && (
                <button
                  type="button"
                  className="pud-btn pud-btn-primary"
                  onClick={() => setIsViewMode(false)}
                >
                  Update
                </button>
              )
            )}
          </div>
        </div>
      )}

      <div className="pud-tab-body">
        {activeTab === "summary" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <CAutocomplete
                label="Scope"
                placeholder={
                  scopeOptionsFilteredForDate.length === 0
                    ? "No scopes available"
                    : "Select scope"
                }
                value={
                  scopeOptionsFilteredForDate.find(
                    (o) => o.scope_id === form.scope?.scope_id
                  ) || null
                }
                onChange={(_e, newValue) =>
                  setForm((prev) => ({ ...prev, scope: newValue || null }))
                }
                options={scopeOptionsFilteredForDate}
                getOptionLabel={(option) => option?.scope_name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.scope_id === value?.scope_id
                }
                disabled={
                  isViewMode || scopeOptionsFilteredForDate.length === 0
                }
              />
              {!isLoadingScopes &&
                scopeOptionsFilteredForDate.length === 0 &&
                scopesData?.scopes?.length > 0 && (
                  <span style={{ color: "#d97706", fontSize: 12 }}>
                    {isCreateMode
                      ? "No remaining scopes for this date"
                      : "No scopes with ongoing bids"}
                  </span>
                )}
            </div>

            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CInput
                  label="Update Title"
                  placeholder="Enter update title"
                  value={form.name}
                  disabled={isViewMode || !selectedScope}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  inputProps={{ maxLength: 100 }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CDatePicker
                  label="Update Date"
                  value={form.date || null}
                  onChange={(date) =>
                    setForm((prev) => ({ ...prev, date: date || null }))
                  }
                  isProjectUpdate
                  allowPastDates
                  disabled={isViewMode || !selectedScope}
                />
              </div>
            </div>

            <CInput
              multiline
              rows={4}
              label="Progress Description"
              placeholder="Write your progress so far"
              value={form.description}
              disabled={isViewMode || !selectedScope}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>
        )}

        {activeTab === "activities" && (
          <UploadSection
            projectId={projectId}
            updateId={effectiveUpdateId}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            activities={availableActivities}
            disabled={isViewMode}
            isCreateMode={isCreateMode}
            hasUpdateId={hasUpdateId}
            activeSubTab={activeSubTab}
            setActiveSubTab={setActiveSubTab}
            hideInternalSubTabs
          />
        )}

        {activeTab === "milestones" && (
          <MilestoneCard
            refDataMilestones={refDataMilestones}
            selectedMilestones={selectedMilestones}
            setSelectedMilestones={setSelectedMilestones}
            milestoneProgress={milestoneProgress}
            onProgressChange={(id, value) =>
              setMilestoneProgress?.((prev) => ({ ...prev, [id]: value }))
            }
            milestoneNotes={milestoneNotes}
            onNotesChange={(id, value) =>
              setMilestoneNotes?.((prev) => ({ ...prev, [id]: value }))
            }
            disabled={isViewMode}
            activities={availableActivities}
            isLoading={isLoadingActivities}
            milestoneIdsFromImages={milestoneIdsFromImages}
            isDisabled={isViewMode || !selectedScope}
          />
        )}

        {activeTab === "issues" && (
          <IssuesSection
            projectId={projectId}
            updateId={effectiveUpdateId}
            activities={availableActivities}
            disabled={isViewMode}
          />
        )}

        {activeTab === "review" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <div style={{ fontSize: 12, color: "#949daa" }}>Update Title</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#0d0d0e" }}>
                  {form.name || "—"}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <div style={{ fontSize: 12, color: "#949daa" }}>Update Date</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#0d0d0e" }}>
                  {form.date ? moment(form.date).format("MMM DD, YYYY") : "—"}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <div style={{ fontSize: 12, color: "#949daa" }}>Scope</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#0d0d0e" }}>
                  {form.scope?.scope_name || "—"}
                </div>
              </div>
            </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <div style={{ fontSize: 12, color: "#949daa" }}>
                Progress Description
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#0d0d0e",
                  whiteSpace: "pre-wrap",
                }}
              >
                {form.description || "—"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8, marginTop: 12,  color: "#0d0d0e" }}>
                Activity Photos
              </div>
              {uploadedFiles && uploadedFiles.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 12,
                  }}
                >
                  {uploadedFiles.map((f) => {
                    const ext = f.file_extention || f.extension || "";
                    const isImg =
                      typeof ext === "string" && ext.startsWith("image/");
                    const src = f.url || f.path;
                    return (
                      <div
                        key={f.uniqueId || f.id}
                        style={{
                          aspectRatio: "1",
                          borderRadius: 10,
                          overflow: "hidden",
                          background: "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isImg && src ? (
                          <img
                            src={src}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: 12, color: "#949daa" }}>
                            File
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#949daa" }}>
                  No photos uploaded
                </div>
              )}
            </div>

            <div style={{marginTop: 12}}>
              {selectedMilestones?.length === 0 ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8,  color: "#0d0d0e" }}>
                    Impacted Milestones
                  </div>
                  <div style={{ fontSize: 13, color: "#949daa" }}>
                    No milestones selected
                  </div>
                </>
              ) : (
                <MilestoneCard
                  refDataMilestones={refDataMilestones}
                  selectedMilestones={selectedMilestones}
                  setSelectedMilestones={() => {}}
                  milestoneProgress={milestoneProgress}
                  onProgressChange={() => {}}
                  milestoneNotes={milestoneNotes}
                  onNotesChange={() => {}}
                  disabled
                  activities={selectedMilestones}
                  isLoading={false}
                  milestoneIdsFromImages={milestoneIdsFromImages}
                  isDisabled
                />
              )}
            </div>

            <div style={{marginTop: 12}}>
              <IssuesSection
                projectId={projectId}
                updateId={effectiveUpdateId}
                activities={availableActivities}
                disabled
                reviewMode
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
